import { RefreshCw } from 'lucide-react';
import { useCallback } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ArticleForm } from '../hooks/useArticleSheet.hook';
import { MarkdownEditor } from './MarkdownEditor/MarkdownEditor';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/Domain/components/Shared/Form/Form';
import { Input } from '@/Domain/components/Shared/Input/Input';

// Mirrors MAX_ARTICLE_TITLE_LENGTH in appquickchat/api/knowledge_base/config.py
const ARTICLE_TITLE_MAX_LENGTH = 2000;

interface ArticleFormProps {
  form: UseFormReturn<ArticleForm>;
  readOnly?: boolean;
  onUnlock?: () => void;
  isUnlocking?: boolean;
}

export const ArticleFormContent = ({
  form: _form,
  readOnly = false,
  onUnlock,
  isUnlocking = false,
}: ArticleFormProps) => {
  const { t } = useTranslation(['knowledge_base']);
  const handleContentNormalized = useCallback(
    (normalized: string) => {
      const current = _form.getValues();
      if (current.content === normalized) return;
      _form.reset({ ...current, content: normalized });
    },
    [_form],
  );

  return (
    <section className="flex lg:h-full min-h-0 min-w-0 flex-col space-y-8 pt-4 pb-2 lg:pt-6 lg:pb-0">
      {readOnly && (
        <div
          className="sticky top-0 z-10 flex items-center justify-between gap-3 rounded-lg border border-warning/30 bg-warning-muted px-3 py-2"
          data-testid="article-auto-refresh-lock"
        >
          <div className="flex items-start gap-2">
            <RefreshCw className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
            <p className="text-xs text-warning-foreground">
              {t('knowledge_base:auto_refresh.locked_banner')}
            </p>
          </div>
          {onUnlock && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={isUnlocking}
              onClick={onUnlock}
              className="shrink-0"
            >
              {t('knowledge_base:auto_refresh.unlock_button')}
            </Button>
          )}
        </div>
      )}

      <div className="min-w-0">
        <FormField
          name="title"
          rules={{
            maxLength: {
              value: ARTICLE_TITLE_MAX_LENGTH,
              message: t('knowledge_base:article.title_too_long', {
                max: ARTICLE_TITLE_MAX_LENGTH,
              }),
            },
          }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('knowledge_base:article.title')}</FormLabel>
              <FormDescription>{t('knowledge_base:article.title_description')}</FormDescription>
              <FormControl>
                <Input
                  placeholder={t('knowledge_base:article.title_placeholder')}
                  {...field}
                  value={field.value ?? ''}
                  disabled={readOnly}
                  data-testid="article-title-input"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="flex-1 flex flex-col min-w-0 min-h-0 pr-1 !custom-scroll">
        <FormField
          name="content"
          render={({ field }) => (
            <FormItem className="flex-1 flex flex-col min-w-0 min-h-0 custom-scroll">
              <FormLabel>{t('knowledge_base:article.content')}</FormLabel>
              <FormDescription>{t('knowledge_base:article.content_description')}</FormDescription>
              <FormControl>
                <MarkdownEditor
                  field={field}
                  placeholder={t('knowledge_base:article.content_placeholder')}
                  onContentNormalized={handleContentNormalized}
                  disabled={readOnly}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </section>
  );
};
